import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CommandLineIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  BoltIcon,
  ClockIcon,
  TruckIcon,
  MapPinIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  CpuChipIcon,
  SignalIcon,
  BellIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Alert } from '@/components/ui/Alert';
import { gpsApi, VehicleTracking, GPSDevice } from '@/api/gpsApi';
import { getVehicles, Vehicle } from '@/api/fleetApi';
import VehicleMapModal from '@/components/transport/VehicleMapModal';
import VehiclePlaybackModal from '@/components/transport/VehiclePlaybackModal';
import toast from 'react-hot-toast';

interface VehicleWithGPS extends Vehicle {
  gpsDevice?: GPSDevice;
  tracking?: VehicleTracking;
  gpsDeviceId?: string;
}

interface CommandEntry {
  id: string;
  timestamp: Date;
  vehicle: string;
  imei: string;
  command: string;
  status: 'sending' | 'success' | 'failed';
  response?: string;
}

const VehicleControlCenter: React.FC = () => {
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleWithGPS | null>(null);
  const [isEmergencyMode, setIsEmergencyMode] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showPlayback, setShowPlayback] = useState(false);
  const [commandHistory, setCommandHistory] = useState<CommandEntry[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const queryClient = useQueryClient();

  // Fetch fleet vehicles
  const { data: fleetData, isLoading: fleetLoading } = useQuery({
    queryKey: ['control-fleet'],
    queryFn: () => getVehicles(),
    refetchInterval: autoRefresh ? 30000 : false
  });

  // Fetch GPS devices
  const { data: gpsData, isLoading: gpsLoading } = useQuery({
    queryKey: ['control-gps-devices'],
    queryFn: () => gpsApi.getDeviceList(),
    refetchInterval: autoRefresh ? 30000 : false
  });

  // Fetch real-time tracking
  const { data: trackingData, isLoading: trackingLoading } = useQuery({
    queryKey: ['control-tracking'],
    queryFn: async () => {
      const vehicles = fleetData?.data?.vehicles || [];
      const gpsVehicles = vehicles.filter((v: Vehicle) => 
        v.isTracking && v.engineNumber?.startsWith('GPS:')
      );
      
      if (gpsVehicles.length === 0) return { data: { devices: [] } };
      
      const imeis = gpsVehicles.map((v: Vehicle) => 
        v.engineNumber!.replace('GPS:', '')
      );
      
      return await gpsApi.getDeviceTracking(imeis);
    },
    refetchInterval: autoRefresh ? 5000 : false,
    enabled: !!fleetData?.data?.vehicles
  });

  // Link vehicles with GPS data
  const vehiclesWithGPS: VehicleWithGPS[] = React.useMemo(() => {
    const vehicles = fleetData?.data?.vehicles || [];
    const gpsDevices = gpsData?.devices || [];
    const trackingDevices = trackingData?.devices || [];
    
    return vehicles
      .filter((v: Vehicle) => v.isTracking && v.engineNumber?.startsWith('GPS:'))
      .map((vehicle: Vehicle) => {
        const gpsDeviceId = vehicle.engineNumber!.replace('GPS:', '');
        const gpsDevice = gpsDevices.find((d: GPSDevice) => d.imei === gpsDeviceId);
        const tracking = trackingDevices.find((t: VehicleTracking) => t.imei === gpsDeviceId);
        
        return {
          ...vehicle,
          gpsDeviceId,
          gpsDevice,
          tracking
        };
      });
  }, [fleetData, gpsData, trackingData]);

  // Command mutation
  const commandMutation = useMutation({
    mutationFn: ({ imei, command }: { imei: string; command: string }) =>
      gpsApi.sendCommand(imei, command),
    onMutate: ({ imei, command }) => {
      const vehicle = vehiclesWithGPS.find(v => v.gpsDeviceId === imei);
      const newEntry: CommandEntry = {
        id: Date.now().toString(),
        timestamp: new Date(),
        vehicle: vehicle?.vehicleNumber || `Vehicle ${imei.slice(-4)}`,
        imei,
        command,
        status: 'sending'
      };
      
      setCommandHistory(prev => [newEntry, ...prev.slice(0, 9)]);
      return { entryId: newEntry.id };
    },
    onSuccess: (response, variables, context) => {
      setCommandHistory(prev => 
        prev.map(entry => 
          entry.id === context?.entryId 
            ? { ...entry, status: 'success' as const, response: response.data.command }
            : entry
        )
      );
      toast.success('Command executed successfully');
      queryClient.invalidateQueries({ queryKey: ['control-tracking'] });
    },
    onError: (_, variables, context) => {
      setCommandHistory(prev => 
        prev.map(entry => 
          entry.id === context?.entryId 
            ? { ...entry, status: 'failed' as const }
            : entry
        )
      );
      toast.error('Command failed');
    }
  });

  const executeCommand = (command: string, vehicle: VehicleWithGPS) => {
    if (!vehicle.gpsDeviceId) {
      toast.error('No GPS device found for this vehicle');
      return;
    }
    commandMutation.mutate({ imei: vehicle.gpsDeviceId, command });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OK': return 'bg-emerald-500';
      case 'OFFLINE': return 'bg-red-500';
      case 'BLOCKED': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const getSpeedColor = (speed: number) => {
    if (speed > 60) return 'text-red-400';
    if (speed > 30) return 'text-yellow-400';
    if (speed > 0) return 'text-green-400';
    return 'text-gray-400';
  };

  if (fleetLoading || gpsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="text-white mt-4">Initializing Vehicle Control Center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent flex items-center">
                <CpuChipIcon className="w-10 h-10 text-blue-400 mr-4" />
                Vehicle Control Center
              </h1>
              <p className="text-slate-300 mt-2 text-lg">
                Advanced GPS Fleet Command & Control System
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-4 py-2 rounded-lg border transition-all ${
                  autoRefresh 
                    ? 'border-green-500 bg-green-500/20 text-green-400' 
                    : 'border-gray-500 bg-gray-500/20 text-gray-400'
                }`}
              >
                <SignalIcon className="w-5 h-5 mr-2 inline" />
                {autoRefresh ? 'Live' : 'Paused'}
              </button>
              
              <div className={`px-6 py-3 rounded-lg border-2 flex items-center ${
                isEmergencyMode 
                  ? 'border-red-500 bg-red-500/20' 
                  : 'border-emerald-500 bg-emerald-500/20'
              }`}>
                <div className={`w-3 h-3 rounded-full mr-3 ${
                  isEmergencyMode 
                    ? 'bg-red-500 animate-pulse' 
                    : 'bg-emerald-500 animate-pulse'
                }`} />
                <span className={`font-bold ${
                  isEmergencyMode ? 'text-red-400' : 'text-emerald-400'
                }`}>
                  {isEmergencyMode ? 'EMERGENCY ACTIVE' : 'SYSTEM READY'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Fleet Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <div className="flex items-center">
              <TruckIcon className="w-8 h-8 text-blue-400" />
              <div className="ml-4">
                <p className="text-slate-400 text-sm">Total Vehicles</p>
                <p className="text-2xl font-bold text-white">{vehiclesWithGPS.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-white rounded-full"></div>
              </div>
              <div className="ml-4">
                <p className="text-slate-400 text-sm">Online</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {vehiclesWithGPS.filter(v => v.tracking?.status.dataStatus === 'OK').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <div className="flex items-center">
              <ChartBarIcon className="w-8 h-8 text-purple-400" />
              <div className="ml-4">
                <p className="text-slate-400 text-sm">Avg Speed</p>
                <p className="text-2xl font-bold text-white">
                  {vehiclesWithGPS.filter(v => v.tracking).length > 0 
                    ? Math.round(vehiclesWithGPS.reduce((sum, v) => sum + (v.tracking?.location.speed || 0), 0) / vehiclesWithGPS.filter(v => v.tracking).length)
                    : 0
                  } km/h
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <div className="flex items-center">
              <BellIcon className="w-8 h-8 text-orange-400" />
              <div className="ml-4">
                <p className="text-slate-400 text-sm">Commands</p>
                <p className="text-2xl font-bold text-orange-400">{commandHistory.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Control Interface */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          
          {/* Vehicle Fleet */}
          <div className="xl:col-span-2">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <TruckIcon className="w-6 h-6 text-blue-400 mr-3" />
                  Active Fleet
                </h2>
                <div className="text-sm text-slate-400">
                  {vehiclesWithGPS.filter(v => v.tracking?.status.dataStatus === 'OK').length} of {vehiclesWithGPS.length} online
                </div>
              </div>

              {vehiclesWithGPS.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {vehiclesWithGPS.map((vehicle: any) => (
                    <div
                      key={vehicle.id}
                      className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                        selectedVehicle?.id === vehicle.id
                          ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20'
                          : 'border-slate-600 bg-slate-700/50 hover:border-slate-500 hover:bg-slate-700/80'
                      }`}
                      onClick={() => setSelectedVehicle(vehicle)}
                    >
                      {/* Status Indicator */}
                      <div className="absolute top-4 right-4">
                        <div className={`w-3 h-3 rounded-full ${
                          vehicle.tracking?.status.dataStatus === 'OK' 
                            ? 'bg-emerald-500 animate-pulse' 
                            : 'bg-red-500'
                        }`} />
                      </div>

                      {/* Vehicle Header */}
                      <div className="mb-4">
                        <h3 className="text-lg font-bold text-white">{vehicle.vehicleNumber}</h3>
                        <p className="text-slate-400 text-sm">{vehicle.make} {vehicle.vehicleModel}</p>
                        <p className="text-slate-500 text-xs">GPS: {vehicle.gpsDeviceId || 'Not configured'}</p>
                      </div>

                      {vehicle.tracking ? (
                        <>
                          {/* Status Badge */}
                          <div className="mb-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              vehicle.tracking.status.dataStatus === 'OK'
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                : 'bg-red-500/20 text-red-400 border border-red-500/30'
                            }`}>
                              {vehicle.tracking.status.dataStatus}
                            </span>
                          </div>

                          {/* Vehicle Metrics */}
                          <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                              <div className={`text-xl font-bold ${getSpeedColor(vehicle.tracking.location.speed)}`}>
                                {vehicle.tracking.location.speed}
                              </div>
                              <div className="text-xs text-slate-400">km/h</div>
                            </div>
                            <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                              <div className={`text-xl font-bold ${
                                vehicle.tracking.status.acc === 'ON' ? 'text-emerald-400' : 'text-red-400'
                              }`}>
                                {vehicle.tracking.status.acc}
                              </div>
                              <div className="text-xs text-slate-400">Engine</div>
                            </div>
                            <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                              <div className="text-xl font-bold text-white">
                                {vehicle.tracking.status.battery || '--'}
                              </div>
                              <div className="text-xs text-slate-400">Battery</div>
                            </div>
                          </div>

                          {/* Location Info */}
                          <div className="text-xs text-slate-400">
                            📍 {vehicle.tracking.location.latitude.toFixed(4)}, {vehicle.tracking.location.longitude.toFixed(4)}
                          </div>
                          
                          {/* Last Update */}
                          <div className="text-xs text-slate-500 mt-1">
                            Updated: {new Date(vehicle.tracking.timing.gpsTime || '').toLocaleTimeString()}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-6">
                          <SignalIcon className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                          <p className="text-slate-400 text-sm">No GPS signal</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <TruckIcon className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No GPS-enabled vehicles found</p>
                </div>
              )}
            </div>
          </div>

          {/* Control Panel */}
          <div className="space-y-6">
            {/* Vehicle Controls */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <CommandLineIcon className="w-6 h-6 text-purple-400 mr-3" />
                Vehicle Controls
              </h2>

              {selectedVehicle ? (
                <div className="space-y-4">
                  {/* Selected Vehicle Info */}
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <h3 className="font-bold text-white">{selectedVehicle.vehicleNumber}</h3>
                    <p className="text-slate-400 text-sm">{selectedVehicle.make} {selectedVehicle.vehicleModel}</p>
                    <p className="text-slate-500 text-xs">IMEI: {selectedVehicle.gpsDeviceId}</p>
                    {selectedVehicle.tracking && (
                      <div className="mt-2 flex items-center space-x-4 text-xs">
                        <span className={`${getSpeedColor(selectedVehicle.tracking.location.speed)}`}>
                          {selectedVehicle.tracking.location.speed} km/h
                        </span>
                        <span className={selectedVehicle.tracking.status.acc === 'ON' ? 'text-emerald-400' : 'text-red-400'}>
                          Engine {selectedVehicle.tracking.status.acc}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Emergency Controls */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-white">Emergency Controls</h4>
                    
                    <Button
                      onClick={() => {
                        if (confirm('EMERGENCY: Stop engine immediately?')) {
                          setIsEmergencyMode(true);
                          executeCommand('RELAY,1', selectedVehicle);
                          setTimeout(() => setIsEmergencyMode(false), 5000);
                        }
                      }}
                      variant="danger"
                      className="w-full bg-red-600 hover:bg-red-700 border-red-500"
                      disabled={commandMutation.isPending}
                    >
                      <StopIcon className="w-4 h-4 mr-2" />
                      EMERGENCY STOP
                    </Button>
                    
                    <Button
                      onClick={() => executeCommand('RELAY,0', selectedVehicle)}
                      variant="primary"
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      disabled={commandMutation.isPending}
                    >
                      <PlayIcon className="w-4 h-4 mr-2" />
                      Restore Engine
                    </Button>
                    
                    <Button
                      onClick={() => executeCommand('UNLOCKDOOR', selectedVehicle)}
                      variant="secondary"
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      disabled={commandMutation.isPending}
                    >
                      <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
                      Unlock Doors
                    </Button>
                  </div>

                  {/* GPS Controls */}
                  <div className="space-y-3 border-t border-slate-600 pt-4">
                    <h4 className="font-medium text-white">GPS Tracking</h4>
                    
                    <Button
                      onClick={() => setShowMap(true)}
                      variant="ghost"
                      className="w-full border-blue-500/30 hover:bg-blue-500/20"
                      disabled={!selectedVehicle.tracking}
                    >
                      <MapPinIcon className="w-4 h-4 mr-2" />
                      View Live Map
                    </Button>
                    
                    <Button
                      onClick={() => setShowPlayback(true)}
                      variant="ghost"
                      className="w-full border-purple-500/30 hover:bg-purple-500/20"
                    >
                      <ClockIcon className="w-4 h-4 mr-2" />
                      GPS Playback
                    </Button>
                    
                    <Button
                      onClick={() => executeCommand('RESET', selectedVehicle)}
                      variant="ghost"
                      className="w-full border-slate-500/30 hover:bg-slate-500/20"
                      disabled={commandMutation.isPending}
                    >
                      <BoltIcon className="w-4 h-4 mr-2" />
                      Reset GPS
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <CommandLineIcon className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Select a vehicle to access controls</p>
                  <p className="text-slate-500 text-sm mt-2">Click on any vehicle card to begin</p>
                </div>
              )}
            </div>

            {/* Command History */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <ClockIcon className="w-5 h-5 text-slate-400 mr-2" />
                Recent Commands
              </h3>

              {commandHistory.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {commandHistory.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-3 ${
                          entry.status === 'success' ? 'bg-emerald-400' : 
                          entry.status === 'failed' ? 'bg-red-400' : 'bg-yellow-400 animate-pulse'
                        }`} />
                        <div>
                          <span className="text-white text-sm font-medium">{entry.vehicle}</span>
                          <span className="text-slate-400 text-xs ml-2">• {entry.command}</span>
                        </div>
                      </div>
                      <div className="text-slate-400 text-xs">
                        {entry.timestamp.toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CommandLineIcon className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">No commands executed</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Emergency Protocol Alert */}
        {isEmergencyMode && (
          <div className="fixed bottom-6 right-6 bg-red-600 border border-red-500 rounded-xl p-4 shadow-2xl animate-pulse">
            <div className="flex items-center">
              <ShieldExclamationIcon className="w-6 h-6 text-white mr-3" />
              <div>
                <div className="font-bold text-white">EMERGENCY PROTOCOL ACTIVE</div>
                <div className="text-red-100 text-sm">Vehicle safety commands in progress...</div>
              </div>
            </div>
          </div>
        )}

        {/* GPS Map Modal */}
        {showMap && selectedVehicle && (
          <VehicleMapModal
            isOpen={showMap}
            onClose={() => setShowMap(false)}
            device={selectedVehicle.gpsDevice || { 
              imei: selectedVehicle.gpsDeviceId || '', 
              deviceName: selectedVehicle.vehicleNumber 
            } as GPSDevice}
            trackingData={selectedVehicle.tracking || null}
          />
        )}

        {/* GPS Playback Modal */}
        {showPlayback && selectedVehicle && (
          <VehiclePlaybackModal
            isOpen={showPlayback}
            onClose={() => setShowPlayback(false)}
            device={selectedVehicle.gpsDevice || { 
              imei: selectedVehicle.gpsDeviceId || '', 
              deviceName: selectedVehicle.vehicleNumber 
            } as GPSDevice}
          />
        )}
      </div>
    </div>
  );
};

export default VehicleControlCenter;